/**
 * Design tokens of the portal, shared by the web application (Ant Design theme) and the Keycloak
 * theme (CSS custom properties, e-mail layout). They follow Microsoft's Fluent 2 design language
 * (observed on fluent2.microsoft.design, October 2026): a 16-step brand ramp used through the same
 * alias roles as Fluent (rest 80, hover 70, pressed 40, selected 60, tint 160), pure grey neutrals
 * for surfaces, text and strokes, a 4 px spacing base, 2/4/8/12 px corners and a key + ambient
 * shadow ramp. The deep teal stays the brand (environment, trust); red is reserved for hazardous
 * waste and blocking states, amber for what expires soon.
 */
/** Brand ramp, darkest (10) to lightest (160); step 80 is the brand colour itself. */
export const brandRamp = {
    10: '#031f1c',
    20: '#042a27',
    30: '#063632',
    40: '#07423d',
    50: '#094f49',
    60: '#0b5b55',
    70: '#0d6961',
    80: '#0f766e',
    90: '#2f877f',
    100: '#4a988f',
    110: '#64a8a0',
    120: '#7eb9b1',
    130: '#99c9c3',
    140: '#b4d9d4',
    150: '#d1e9e5',
    160: '#eef9f7',
};
/**
 * Fluent neutral aliases (light theme): grey[N] is N % lightness, so grey[96] is #f5f5f5.
 * Background 1 is the card and header surface, background 3 the page canvas; foreground 1 is body
 * text, 3 secondary text; stroke 1 outlines controls, stroke 2 cards and dividers; the accessible
 * stroke underlines inputs.
 */
export const neutral = {
    background1: '#ffffff',
    background1Hover: '#f5f5f5',
    background1Pressed: '#e0e0e0',
    background1Selected: '#ebebeb',
    background2: '#fafafa',
    background3: '#f5f5f5',
    background4: '#f0f0f0',
    background6: '#e6e6e6',
    foreground1: '#242424',
    foreground2: '#424242',
    foreground3: '#616161',
    foreground4: '#707070',
    foregroundDisabled: '#bdbdbd',
    stroke1: '#d1d1d1',
    stroke1Hover: '#c7c7c7',
    stroke1Pressed: '#b3b3b3',
    stroke2: '#e0e0e0',
    stroke3: '#f0f0f0',
    strokeAccessible: '#616161',
    strokeAccessibleHover: '#575757',
    strokeFocus: '#242424',
    stencil: '#e6e6e6',
};
/**
 * The same roles in Fluent's dark theme (webDarkTheme): the card and header surface is grey 16,
 * the page canvas grey 8, body text white and secondary text grey 68; strokes stay visible on the
 * dark surfaces and the focus stroke turns white.
 */
export const neutralDark = {
    background1: '#292929',
    background1Hover: '#3d3d3d',
    background1Pressed: '#1f1f1f',
    background1Selected: '#383838',
    background2: '#1f1f1f',
    background3: '#141414',
    background4: '#0a0a0a',
    background6: '#333333',
    foreground1: '#ffffff',
    foreground2: '#d6d6d6',
    foreground3: '#adadad',
    foreground4: '#999999',
    foregroundDisabled: '#5c5c5c',
    stroke1: '#666666',
    stroke1Hover: '#757575',
    stroke1Pressed: '#6b6b6b',
    stroke2: '#525252',
    stroke3: '#3d3d3d',
    strokeAccessible: '#adadad',
    strokeAccessibleHover: '#bdbdbd',
    strokeFocus: '#ffffff',
    stencil: '#575757',
};
/**
 * Fluent elevation: a sharp key shadow (y = blur / 2) for the edge and a soft ambient one for the
 * distance. Low ramp 2 to 16 (cards 4, menus and callouts 16), high ramp 28 and 64 (side sheets,
 * dialogs), with the alias shadow colours of the light theme.
 */
export const shadow = {
    shadow2: '0 0 2px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.14)',
    shadow4: '0 0 2px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.14)',
    shadow8: '0 0 2px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.14)',
    shadow16: '0 0 2px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.14)',
    shadow28: '0 0 8px rgba(0, 0, 0, 0.2), 0 14px 28px rgba(0, 0, 0, 0.24)',
    shadow64: '0 0 8px rgba(0, 0, 0, 0.2), 0 32px 64px rgba(0, 0, 0, 0.24)',
};
/** The elevation ramp of the dark theme: Fluent's darker shadow colours, so a card still lifts. */
export const shadowDark = {
    shadow2: '0 0 2px rgba(0, 0, 0, 0.24), 0 1px 2px rgba(0, 0, 0, 0.28)',
    shadow4: '0 0 2px rgba(0, 0, 0, 0.24), 0 2px 4px rgba(0, 0, 0, 0.28)',
    shadow8: '0 0 2px rgba(0, 0, 0, 0.24), 0 4px 8px rgba(0, 0, 0, 0.28)',
    shadow16: '0 0 2px rgba(0, 0, 0, 0.24), 0 8px 16px rgba(0, 0, 0, 0.28)',
    shadow28: '0 0 8px rgba(0, 0, 0, 0.4), 0 14px 28px rgba(0, 0, 0, 0.48)',
    shadow64: '0 0 8px rgba(0, 0, 0, 0.4), 0 32px 64px rgba(0, 0, 0, 0.48)',
};
/**
 * Status colours by role: the mark (an icon, a bar), the text, a quiet background and its border.
 * Danger is reserved for hazardous waste and blocking states, warning for what expires soon.
 */
export const status = {
    danger: '#dc2626',
    dangerText: '#b91c1c',
    dangerBg: '#fee2e2',
    dangerBorder: '#fca5a5',
    warning: '#d97706',
    warningText: '#b45309',
    warningBg: '#fef3c7',
    warningBorder: '#fcd34d',
    success: '#15803d',
    successText: '#15803d',
    successBg: '#dcfce7',
    successBorder: '#bbf7d0',
    info: '#2563eb',
    infoText: '#1d4ed8',
    infoBg: '#dbeafe',
    infoBorder: '#bfdbfe',
    /** an icon that says "nothing here" (not applicable, no address) */
    iconMuted: '#94a3b8',
};
/** The same roles on the dark surfaces: lighter marks and text, deep backgrounds. */
export const statusDark = {
    danger: '#f87171',
    dangerText: '#fca5a5',
    dangerBg: '#3f1d1d',
    dangerBorder: '#7f1d1d',
    warning: '#fbbf24',
    warningText: '#fcd34d',
    warningBg: '#3d2e0b',
    warningBorder: '#78350f',
    success: '#4ade80',
    successText: '#86efac',
    successBg: '#14301d',
    successBorder: '#166534',
    info: '#60a5fa',
    infoText: '#93c5fd',
    infoBg: '#172b4d',
    infoBorder: '#1e3a8a',
    iconMuted: '#6b7280',
};
/**
 * The aliases the portal reads. `sidebar` and its companions remain the dark band of the e-mails
 * and of the Keycloak consoles; the application's own navigation is a light Fluent pane.
 */
export const brand = {
    primary: brandRamp[80],
    primaryDark: brandRamp[70],
    primaryPressed: brandRamp[40],
    primarySelected: brandRamp[60],
    primarySoft: brandRamp[150],
    sidebar: '#0b1f1d',
    sidebarDeep: '#071514',
    sidebarText: '#a7c4c0',
    surface: neutral.background3,
    border: neutral.stroke2,
    text: neutral.foreground1,
    textMuted: neutral.foreground3,
    danger: status.danger,
    warning: status.warning,
    success: status.success,
    info: status.info,
};
/** Tints of the brand for selected and highlighted states, and neutral hovers. */
export const tint = {
    primaryBg: brandRamp[160],
    primaryBgHover: brandRamp[150],
    hover: neutral.background1Hover,
    row: neutral.background1Hover,
    header: neutral.background2,
};
/** The sign-in hero: a radial glow of lighter teal fading into the sidebar colours. */
export const hero = {
    glow: '#134e4a',
    text: '#e6f4f1',
    heading: '#ffffff',
    lead: brand.sidebarText,
    check: '#5eead4',
    footnote: '#6f8f8b',
    gradient: `radial-gradient(1200px 600px at 10% 10%, #134e4a 0%, ${brand.sidebar} 55%, ${brand.sidebarDeep} 100%)`,
};
/**
 * Fluent's approach to type: Segoe UI on Windows, the native system font elsewhere (San Francisco
 * on Apple devices, Roboto on Android), Inter where none is installed (Linux), and IBM Plex Sans
 * Arabic for Arabic where the system font has none.
 */
export const fontFamily = "'Segoe UI Variable Text', 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Inter Variable', Roboto, 'Helvetica Neue', 'IBM Plex Sans Arabic', sans-serif";
/** Font stack for e-mails: mail clients rarely load web fonts, so system fonts only. */
export const emailFontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, Tahoma, sans-serif";
/**
 * Fluent shapes and sizes: 4 px corners by default, 2 px under 32 px, 8 px on large containers
 * (cards, dialogs), 12 px on sheets and popovers; 32 px controls (24 small, 40 large); the Body 1
 * type size, 14/20.
 */
export const shape = {
    fontSize: 14,
    radiusSM: 2,
    radius: 4,
    radiusLG: 8,
    radiusXL: 12,
    controlHeight: 32,
};
/**
 * The Fluent web type ramp, size / line height in px: Caption 1 12/16, Body 1 14/20, Subtitle 2
 * 16/22, Subtitle 1 20/26, Title 3 24/32, Title 2 28/36, Title 1 32/40, Large title 40/52. Regular
 * and semibold (600) weights, sentence case.
 */
export const typeRamp = {
    caption1: [12, 16],
    body1: [14, 20],
    subtitle2: [16, 22],
    subtitle1: [20, 26],
    title3: [24, 32],
    title2: [28, 36],
    title1: [32, 40],
    largeTitle: [40, 52],
};
/** Below this width the sign-in card fills the screen, as Microsoft's sign-in on a phone. */
export const compactBreakpoint = 600;
//# sourceMappingURL=tokens.js.map